import { z } from 'zod'

/**
 * Splits een komma-gescheiden adres-string naar getrimde, niet-lege adressen.
 * Spiegelt de verzendkant (de uitrusting-bestelmail splitst `leverancier.email`
 * exact zo naar meerdere ontvangers), zodat read↔write consistent zijn: wat het
 * formulier accepteert, kan de mailer ook daadwerkelijk versturen.
 */
export function splitEmailList(value: string | null | undefined): string[] {
  if (!value) return []
  return value
    .split(',')
    .map((e) => e.trim())
    .filter((e) => e.length > 0)
}

/**
 * Dedup e-mailadressen case-insensitief (eerste casing wint), volgorde behouden.
 * Eén bron voor zowel de bestelmail-groepering als de uiteindelijke ontvangerlijst,
 * zodat dezelfde lezer nooit dubbel in de `to` belandt (ook niet als hij zowel het
 * leverancier-adres als een per-artikel extra ontvanger is).
 */
export function dedupeEmailsCI(addresses: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const a of addresses) {
    const adres = a.trim()
    if (adres.length === 0) continue
    const key = adres.toLowerCase()
    if (!seen.has(key)) {
      seen.add(key)
      out.push(adres)
    }
  }
  return out
}

const isEmail = (s: string): boolean => z.string().email().safeParse(s).success

/**
 * Zod-veld voor één óf meerdere komma-gescheiden e-mailadressen. Valideert elk
 * adres, normaliseert naar `"a@x.nl, b@y.nl"` en behandelt leeg/whitespace als
 * `null`. `undefined` blijft `undefined` (veld weggelaten → geen wijziging bij
 * PATCH); expliciete `null`/leeg = gewist.
 */
export const multiEmailSchema = z
  .string()
  .trim()
  .max(500)
  .nullable()
  .refine((v) => v == null || splitEmailList(v).every(isEmail), {
    message: 'Eén of meer e-mailadressen zijn ongeldig (scheid meerdere met een komma)',
  })
  .transform((v) => {
    if (v == null) return null
    const parts = splitEmailList(v)
    return parts.length === 0 ? null : parts.join(', ')
  })
  .optional()
